import React, { useEffect, forwardRef, useCallback } from 'react';
import { HamburgerMenuProps, HamburgerMenuIconProps } from './HamburgerMenu.types';
import LanguageSelector from './LanguageSelector';
import { useI18n } from '../../hooks/useI18n';
import {
  StyledDrawer,
  StyledNavigationList,
  NavigationItem,
  LanguageSelectorContainer,
  HamburgerIcon,
} from './Header.styles';

export const HamburgerMenuIcon = forwardRef<HTMLButtonElement, HamburgerMenuIconProps>(
  (
    {
      onClick,
      'aria-expanded': ariaExpanded,
      'aria-controls': ariaControls,
      'aria-label': ariaLabel,
    },
    ref,
  ) => (
    <HamburgerIcon
      ref={ref}
      onClick={onClick}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      aria-label={ariaLabel}
      role="button"
      tabIndex={0}
    />
  ),
);

HamburgerMenuIcon.displayName = 'HamburgerMenuIcon';

const HamburgerMenu: React.FC<HamburgerMenuProps> = React.memo(
  ({
    id,
    isOpen,
    onClose,
    navigationItems,
    onNavigationClick,
    activeItemKey = 'home',
    language,
  }) => {
    const { t } = useI18n();

    const handleMenuClick = (to: string): void => {
      onNavigationClick(to);
      onClose();
    };

    const handleKeyDown = (event: React.KeyboardEvent, to: string): void => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleMenuClick(to);
      }
    };

    const trapFocus = useCallback(
      (event: KeyboardEvent): void => {
        if (!isOpen) return;

        const drawerElement = document.querySelector('[role="dialog"]');
        const focusableElements = drawerElement?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );

        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (event.key === 'Tab') {
          if (event.shiftKey) {
            if (document.activeElement === firstElement) {
              event.preventDefault();
              lastElement.focus();
            }
          } else {
            if (document.activeElement === lastElement) {
              event.preventDefault();
              firstElement.focus();
            }
          }
        }
      },
      [isOpen],
    );

    useEffect(() => {
      if (isOpen) {
        document.addEventListener('keydown', trapFocus);

        setTimeout(() => {
          const drawerElement = document.querySelector('[role="dialog"]');
          const firstFocusable = drawerElement?.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          ) as HTMLElement;

          if (firstFocusable) {
            firstFocusable.focus();
          }
        }, 100);
      } else {
        document.removeEventListener('keydown', trapFocus);
      }

      return () => {
        document.removeEventListener('keydown', trapFocus);
      };
    }, [isOpen, trapFocus]);

    return (
      <StyledDrawer
        title="KIMERAWARE"
        placement="right"
        onClose={onClose}
        open={isOpen}
        size="default"
        keyboard={true}
        maskClosable={true}
      >
        <div id="drawer-title" style={{ position: 'absolute', left: '-9999px' }}>
          {t('accessibility.mainNavigation')}
        </div>
        <div id="drawer-description" style={{ position: 'absolute', left: '-9999px' }}>
          {t('accessibility.mobileMenuExpanded')}
        </div>

        <nav role="navigation" aria-label={t('accessibility.mainNavigation')}>
          <StyledNavigationList>
            {navigationItems.map((item, index) => (
              <NavigationItem
                key={item.key}
                isActive={item.key === activeItemKey}
                onClick={() => handleMenuClick(item.to)}
                onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, item.to)}
                role="button"
                tabIndex={0}
                aria-current={item.key === activeItemKey ? 'page' : undefined}
                aria-label={
                  item.key === activeItemKey
                    ? `${item.label} (${t('accessibility.currentPage')})`
                    : item.label
                }
              >
                {item.label}
              </NavigationItem>
            ))}
          </StyledNavigationList>
        </nav>

        <LanguageSelectorContainer>
          <LanguageSelector />
        </LanguageSelectorContainer>
      </StyledDrawer>
    );
  },
);

HamburgerMenu.displayName = 'HamburgerMenu';

export default HamburgerMenu;
